import React from "react";
import OfferColumnConfigurations from "./offerColumnConfigurations";
import ConfigurableColumnBuilder from "common/agGrid/configurableColumnBuilder";
import Offer from "domain/offer";
import { getMobileOfferTypeName, getOfferTypeShortName, OfferTypeEnum } from "domain/offerType";
import { calculateRequiredFuturesPrice, calculateToHit } from "./priceCalculator";
import { getHedgeOrderTypeName, getOfferContractOrderTypeName, OrderType } from "domain/orderType";
import { formatQuantity, formatOrderDisplayId, formatPricingValue } from "common/formatters";
import { CustomerWillSellText } from "common/formatters/useCustomerWillSellText";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { canShowOrderId, getOrderStatusDisplayName } from "./orderStatus";
import SiteLink from "common/ui/SiteLink";
import { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import { OfferStatus } from "domain/offerStatus";
import { compareDisplayIds } from "common/strings";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getTotalFees } from "./helpers";
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import offerTypeHelper from "domain/offerTypeHelper";
import { UnitConversionRate } from "domain/unit";
import unitHelper from "domain/unitHelper";
import { TranslatorCollection } from "common/translation/useTranslators";
import Merchant from "domain/merchant";
import BasisColumn from "./BasisColumn";
import ProducerColumn from "features/settings/producers/ProducerColumn";
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";
import UiCustomization from 'domain/uiCustomization';
import * as NP from "number-precision";

export class OfferColumn {
    //These strings should never change after publishing.
    //Otherwise it will invalidate user settings
    public static id: string = "id";
    public static customerContractId: string = "customerContractId";
    public static customer: string = "customer";
    public static commodity: string = "commodity";
    public static position: string = "position";
    public static location: string = "location";
    public static inventoryLocation: string = "inventoryLocation";
    public static contractLocation: string = "contractLocation";
    public static customerWillSell: string = "customerWillSell";
    public static offerType: string = "offerType";
    public static orderType: string = "orderType";
    public static hedgeType: string = "hedgeType";
    public static hedgeStatus: string = "hedgeStatus";
    public static quantity: string = "quantity";
    public static uom: string = "uom";
    public static localQuantity: string = "localQuantity";
    public static localUom: string = "localUom";
    public static cashPrice: string = "cashPrice";
    public static finalPrice: string = "finalPrice";
    public static basis: string = "basis";
    public static futuresPrice: string = "futuresPrice";
    public static myBasis: string = "myBasis";
    public static netBasis: string = "netBasis";
    public static contractSymbol: string = "contractSymbol";
    public static toHit: string = "toHit";
    public static offerExpiration: string = "offerExpiration";
    public static finalPricingDate: string = "finalPricingDate";
    public static trader: string = "trader";
    public static lastUpdatedDate: string = "lastUpdatedDate";
    public static lastUpdatedBy: string = "lastUpdatedBy";
    public static comments: string = "comments";
    public static activationTime: string = "activationTime";
    public static cancelledDate: string = "cancelledDate";
    public static fcmAccount: string = "fcmAccount";
    public static fillQuantity: string = "fillQuantity";
    public static fillPrice: string = "fillPrice";
    public static fillTime: string = "fillTime";
    public static producerAccount: string = "producerAccount";
    public static totalFee: string = "totalFee";
    public static deliveryPeriod: string = "deliveryPeriod";
    public static deliveryStart: string = "deliveryStart";
    public static deliveryEnd: string = "deliveryEnd";
    public static deliveryMethod: string = "deliveryMethod";
    public static freightFee: string = "freightFee";
    public static createdDate: string = "createdDate";
    public static createdBy: string = "createdBy";
    public static orderId: string = "orderId";
    public static fcmAccountNumber: string = "fcmAccountNumber";
    public static paymentOption: string = "paymentOption";
    public static currency: string = "currency";

    //Mobile-specific columns
    public static offerType_mobile: string = "offerType_mobile";
    public static price_mobile: string = "price_mobile";
}

function getCashPrice(offer: Offer): number {

    if (offer == null) return null;

    if (offerTypeHelper.isHta(offer.type) ||
        offer.orderType === OrderType.Tas ||
        offer.isPriceAtOpenEnabled) return null;

    return offer.cashPrice
}

function getHType(offer: Offer): string {
    return getHedgeOrderTypeName(offer.orderType === OrderType.Tas ? offer.orderType : offer?.order?.type);
}

function getPrice(offer: Offer): number {
    if (offer.isPriceAtOpenEnabled) return null;

    switch (offer.type) {
        case OfferTypeEnum.Cash:
        case OfferTypeEnum.CashMinPrice:
        case OfferTypeEnum.FixBasisOnHta:
            return offer.cashPrice;
        case OfferTypeEnum.Hta:
        case OfferTypeEnum.HtaMinPrice:
        case OfferTypeEnum.FixFuturesOnBasis:
            return calculateRequiredFuturesPrice(offer);
        default:
            throw new Error(localize("OfferListColumns_Error_UnrecognizedType"));
    }
}

interface OfferIdComponentProps {
    id: number;
    displayId: string;
    status: OfferStatus;
    contractSyncFailureReason?: string;
    rejectionMessage?: string;
    riskRejectionReason?: string;
}

const OfferIdComponent: React.FC<OfferIdComponentProps> = React.memo(props => {

    if (props.displayId == null) return null;

    const isFilled = props.status === OfferStatus.Filled;

    return <span>
        {isFilled && (!props.contractSyncFailureReason ?
            <SiteLink.ContractDetails contractDisplayId={props.displayId} /> :
            <>{props.displayId}</>)
        }
        {!isFilled &&
            <SiteLink.OfferDetails offerDisplayId={props.displayId} />
        }
        {props.riskRejectionReason && <OverlayTrigger placement="auto" trigger="click" rootClose
            overlay={<Tooltip id={"riskrej_" + props.id}>
                <p>{localize("OfferListColumns_Error_UnableToHedge", { reason: props.riskRejectionReason })}</p>
                <p>{localize("OfferListColumns_Error_TakeActionOrDismiss")}</p>
                {!isFilled && <p><strong>{localize("OfferListColumns_Error_OrderRejected")}</strong></p>}
            </Tooltip>}>
            <i className="clickable pl-1 text-danger fa fa-exclamation-triangle"></i>
        </OverlayTrigger>
        }
        {props.rejectionMessage && <OverlayTrigger placement="auto" trigger="click" rootClose
            overlay={<Tooltip id={"orderRej_" + props.id}>
                <p>{props.rejectionMessage}</p>
            </Tooltip>}>
            <i className="clickable pl-1 text-danger fa fa-exclamation-triangle"></i>
        </OverlayTrigger>
        }
        {props.contractSyncFailureReason && <OverlayTrigger placement="auto" trigger="click" rootClose
            overlay={<Tooltip id={"contractRej_" + props.id}>
                <p>{localize("OfferListColumns_Error_CannotCreateContract", { reason: props.contractSyncFailureReason })}</p>
            </Tooltip>}>
            <i className="clickable pl-1 text-danger fa fa-exclamation-triangle"></i>
        </OverlayTrigger>
        }
    </span>;
});




export class OfferListColumnsFilter {
    offerStatus: OfferStatus = OfferStatus.Working;
    isCustomerEditAllowed: boolean = false;
    onCustomerSelected: any = null;
    isCustomer: boolean = false;
    isMobile: boolean = false;
    conversionRates: UnitConversionRate[] = [];
    translators: TranslatorCollection = null;
    showQuantityUnits: boolean = false;
    merchant: Merchant = null;
}

export function getColumnBuilder(filter: OfferListColumnsFilter, uiCustomization: UiCustomization, customerWillSellText: CustomerWillSellText): ConfigurableColumnBuilder<Offer> {
    const builder = new ConfigurableColumnBuilder<Offer>();
    const defaultClass = filter.isMobile ? "mobile" : "";
    const quantityPrecision = uiCustomization.offersContractsQuantityDecimalPrecision;

    //Default columns
    builder.for(OfferColumn.id, localize("Column_Header_OfferId"), null, localize("Column_Description_OfferId"))
        .withGetter(m => m)
        .withFilterGetter(m => m.displayId)
        .withCellRenderer(params => <OfferIdComponent
            id={params.node.data.id}
            displayId={params.node.data.displayId}
            status={params.node.data.status}
            contractSyncFailureReason={params.node.data.contractSyncFailureReason}
            rejectionMessage={params.node.data.order?.rejectionMessage}
            riskRejectionReason={params.node.data.order?.riskRejectionReason}
        />)
        //refresh the column if the alert is acknowledged
        .withEquals((o1, o2) => {
            return o1.data?.displayId === o2.data?.displayId && o1.data?.order?.riskRejectionReason === o2.data?.order?.riskRejectionReason
        })
        .withComparator((o1, o2) => compareDisplayIds(o1.data?.displayId, o2.data?.displayId))
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 55 : 95);
    builder.for(OfferColumn.customerContractId, localize("Details_Label_CustomerContractId"), "customerContractId", localize("Column_Description_CustomerContractId")
        , { hide: !filter.merchant.generalSettings.displayCustomerContractIdForBuy && !filter.merchant.generalSettings.displayCustomerContractIdForSell })
        .withWidth(135);
    builder.for(OfferColumn.customer, localize("Column_Header_Customer"), null, "", { hide: filter.isCustomer })
        .withGetter(params => getProducerDisplayName(params.data?.producer))
        .withCellRenderer(params => <ProducerColumn name={params.value}
            isEditAllowed={filter.isCustomerEditAllowed}
            isActive={params.node.data.producer.isActive}
            onSelected={() => filter.onCustomerSelected(params.node.data.producer)} />)
        .withWidth(150);
    builder.for(OfferColumn.commodity, localize("Column_Header_Commodity"), "deliveryPeriod.commodity.name")
        .withWidth(100);
    builder.for(OfferColumn.position, localize("Column_Header_Position"), "positionSetting.displayName", localize("Column_Description_Position"), { hide: filter.isCustomer })
        .withWidth(80);
    builder.for(OfferColumn.location, localize("Column_Header_Location"), "location.name", localize("Column_Description_Location"))
        .withFormatter(m => m ?? localizeCommon("Value_DetermineLater"))
        .withWidth(150);
    builder.for(OfferColumn.inventoryLocation, localize("Column_Header_InventoryLocation"), "inventoryLocation.name", localize("Column_Description_InventoryLocation"), { hide: !filter.merchant.generalSettings.displayInventoryLocation })
        .withFormatter(m => m ?? localizeCommon("Value_DetermineLater"))
        .withWidth(150);
    builder.for(OfferColumn.contractLocation, localize("Column_Header_ContractLocation"), "contractLocation.name", localize("Column_Description_ContractLocation"), { hide: !filter.merchant.generalSettings.displayContractLocation })
        .withFormatter(m => m ?? localizeCommon("Value_DetermineLater"))
        .withWidth(150);
    builder.for(OfferColumn.customerWillSell, customerWillSellText.columnHeader, "customerWillSell", customerWillSellText.columnDescription)
        .withFormatter(customerWillSellText.formatValue)
        .withWidth(60);
    if (filter.isMobile) {
        builder.for(OfferColumn.offerType_mobile, localize("Column_Header_Type"), "type", localize("Column_Description_OfferType"))
            .withFormatter(getMobileOfferTypeName)
            .withColumnClass(defaultClass, defaultClass)
            .withWidth(40);
    }
    else {
        builder.for(OfferColumn.offerType, localize("Column_Header_Type"), "type", localize("Column_Description_OfferType"))
            .withFormatter(getOfferTypeShortName)
            .withWidth(70);
    }
    builder.for(OfferColumn.orderType, localize("Column_Header_PricingType"), "orderType", localize("Column_Description_PricingType"))
        .withFormatter(getOfferContractOrderTypeName)
        .withWidth(80);
    builder.for(OfferColumn.hedgeType, localize("Column_Header_HedgeOrderType"), null, localize("Column_Description_HedgeOrderType"), { hide: filter.isCustomer })
        .withGetter(params => getHType(params.node.data))
        .withWidth(80);
    builder.for(OfferColumn.hedgeStatus, localize("Column_Header_HedgeOrderStatus"), null, localize("Column_Description_HedgeOrderStatus"), { hide: filter.isCustomer })
        .withGetter(params => getOrderStatusDisplayName(params.data?.order?.status))
        .withWidth(80);

    builder.for(OfferColumn.quantity, localize("Column_Header_Quantity"), "quantity", localize("Column_Description_OfferQuantity"), { aggFunc: "npSum" })
        .withFormatterParams(params => !filter.showQuantityUnits
            ? formatQuantity(params.value, quantityPrecision)
            : unitHelper.getQuantityTextWithUnits(params.value, params.data?.deliveryPeriod?.commodity, filter.conversionRates, quantityPrecision))
        .withTooltipValueGetter(params => !filter.showQuantityUnits && params.data != null
            ? unitHelper.getLocalQuantityTextWithUnits(params.value, params.data.deliveryPeriod?.commodity, filter.conversionRates, quantityPrecision)
            : null)
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 50 : 120);
    builder.forExportOnly(OfferColumn.uom, localize("Column_Header_BaseUnit"))
        .withWidth(30);
    builder.forExportOnly(OfferColumn.localQuantity, localize("Column_Header_LocalQuantity"))
        .withWidth(120);
    builder.forExportOnly(OfferColumn.localUom, localize("Column_Header_LocalUnit"))
        .withWidth(30);
    builder.for(OfferColumn.cashPrice, localize("Column_Header_CashPrice"), null, localize("Column_Description_OfferCashPrice"))
        .withGetter(params => unitHelper.getLocalPriceText(getCashPrice(params.data), params.data.deliveryPeriod?.commodity, filter.conversionRates))
        .withWidth(120);
    builder.for(OfferColumn.finalPrice, localize("Column_Header_FinalPrice"), null, localize("Column_Description_OfferFinalPrice"))
        .withGetter(params => unitHelper.getLocalPriceText(params.data?.finalPrice, params.data.deliveryPeriod?.commodity, filter.conversionRates))
        .withWidth(120);
    builder.for(OfferColumn.totalFee, localize("Column_Header_TotalFees"), null, localize("Column_Description_TotalFees"))
        .withGetter(params => getTotalFees(params.node.data))
        .withTooltipValueGetter(params => params.data != null ? unitHelper.getLocalPrice(getTotalFees(params.data), params.data.deliveryPeriod?.commodity, filter.conversionRates) : null)
        .asDecimal()
        .asRightAligned()
        .withWidth(60);

    builder.for(OfferColumn.currency, localize("Column_Header_Currency"), "currency.code", localize("Column_Description_Currency"))
        .asRightAligned()
        .withWidth(filter.isMobile ? 50 : 80);

    if (filter.isMobile) {
        builder.for(OfferColumn.price_mobile, localize("Column_Header_Price"), null, "")
            .withGetter(params => getPrice(params.node.data))
            .withFormatter(formatPricingValue)
            .withColumnClass(defaultClass, defaultClass)
            .asRightAligned()
            .withWidth(55);
    }
    builder.for(OfferColumn.basis, localize("Column_Header_ActualBasis"), "basis", localize("Column_Description_ActualBasis"))
        .asRightAligned()
        .withCellRenderer(params => <BasisColumn value={params.value} isFixed={params.node.data.isBasisFixed} />)
        .withWidth(80);
    builder.for(OfferColumn.futuresPrice, localize("Column_Header_RequiredFuturesPrice"), null, localize("Column_Description_RequiredFuturesPrice"))
        .withGetter(params => params.data == null ? null : calculateRequiredFuturesPrice(params.data))
        .withFormatter(formatPricingValue)
        .withWidth(80);
    builder.for(OfferColumn.myBasis, localize("Column_Header_PostedBasis"), null, localize("Column_Description_PostedBasis"))
        .withGetter(params => offerTypeHelper.isHta(params.node.data.type) || params.node.data.deliveryPeriod.commodity.isCashOnly ? null : params.node.data.currentBasis)
        .withFormatter(formatPricingValue)
        .asRightAligned()
        .withWidth(100);
    builder.for(OfferColumn.netBasis, localize("Column_Header_NetBasis"), null, localize("Column_Description_NetBasis"))
        .withGetter(params => !offerTypeHelper.hasBasis(params.node.data.type) || params.node.data.deliveryPeriod.commodity.isCashOnly ? null : NP.minus(params.node.data.basis ?? 0, params.node.data.freightFee ?? 0))
        .withFormatter(formatPricingValue)
        .asRightAligned()
        .withWidth(100);
    builder.for(OfferColumn.contractSymbol, localize("Column_Header_FuturesSymbol"), "futuresContract.symbol", localize("Column_Description_FuturesSymbol"))
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 50 : 65);
    if (filter.offerStatus === OfferStatus.Working || filter.offerStatus === OfferStatus.Pending) {
        builder.for(OfferColumn.toHit, localize("Column_Header_ToHit"), null, localize("Column_Description_ToHit"))
            .withGetter(params => {
                if (params.data == null) return null;
                if (params.node.data.orderType === OrderType.Tas) return null;
                return calculateToHit(params.data);
            })
            .asDecimal()
            .asRightAligned()
            .withWidth(filter.isMobile ? 78 : 100);
    }


    builder.for(OfferColumn.offerExpiration, localize("Column_Header_Expiration"), "expirationDate", localize("Column_Description_OfferExpiration"))
        .asUtcDate()
        .withWidth(70);
    builder.for(OfferColumn.finalPricingDate, localize("Column_Header_FinalPricingDate"), "finalPricingDate", localize("Column_Description_FinalPricingDate"))
        .asUtcDate()
        .withWidth(70);
    builder.for(OfferColumn.trader, localize("Column_Header_Trader"), "trader.lastName")
        .withWidth(100);
    builder.for(OfferColumn.createdDate, localize("Column_Header_CreatedDate"), "createdDate", localize("Column_Description_CreatedDate"))
        .asDateTime()
        .withWidth(150);
    builder.for(OfferColumn.lastUpdatedDate, localize("Column_Header_LastUpdate"), "lastUpdatedDate", localize("Column_Description_LastUpdate"))
        .asDateTime()
        .withWidth(150);


    if (filter.offerStatus === OfferStatus.Cancelled) {
        builder.for(OfferColumn.cancelledDate, localize("Column_Header_CancellationTime"), "order.cancelationTime", localize("Column_Description_CancellationTime"))
            .asDateTime();
    }

    //extra columns
    builder.for(OfferColumn.comments, localize("Column_Header_Comment"), "comments", localize("Column_Description_Comment"), { hide: filter.isCustomer })
        .withWidth(150);
    builder.for(OfferColumn.lastUpdatedBy, localize("Column_Header_ModifiedBy"), "lastUpdatedBy.name", "");
    builder.for(OfferColumn.fillPrice, localize("Column_Header_OrderFilledPrice"), "order.fillPrice", localize("Column_Description_OrderFilledPrice"), { hide: filter.isCustomer })
        .asDecimal()
        .withWidth(80);
    builder.for(OfferColumn.fillQuantity, localize("Column_Header_OrderFilledQuantity"), "order.fillQuantity", localize("Column_Description_OrderFilledQuantity"), { hide: filter.isCustomer })
        .withFormatter(v => formatQuantity(v, quantityPrecision))
        .withWidth(80);
    builder.for(OfferColumn.fillTime, localize("Column_Header_OfferFillTime"), "fillTime", localize("Column_Description_OfferFillTime"), { hide: filter.isCustomer })
        .asDateTime()
        .withWidth(150);
    builder.for(OfferColumn.producerAccount, localize("Column_Header_AccountId"), null, localize("Column_Description_AccountId"))
        .withGetter(params => getProducerAccountDisplayName(params.data?.customerAccount))
        .withWidth(160);
    builder.for(OfferColumn.deliveryPeriod, localize("Column_Header_DeliveryPeriod"), null, localize("Column_Description_DeliveryPeriod"))
        .withGetter(params => getDeliveryPeriodDisplayName(params.data?.deliveryPeriod))
        .withWidth(195);
    builder.for(OfferColumn.deliveryStart, localize("Column_Header_DeliveryStart"), null, localize("Column_Description_DeliveryStart"))
        .withGetter(params => params.data?.deliveryPeriodStartDate ?? params.data?.deliveryPeriod.start)
        .asUtcDate()
        .withWidth(90);
    builder.for(OfferColumn.deliveryEnd, localize("Column_Header_DeliveryEnd"), null, localize("Column_Description_DeliveryEnd"))
        .withGetter(params => params.data?.deliveryPeriodEndDate ?? params.data?.deliveryPeriod.end)
        .asUtcDate()
        .withWidth(90);
    builder.for(OfferColumn.deliveryMethod, localize("Column_Header_DeliveryMethod"), null, localize("Column_Description_DeliveryMethod"))
        .withGetter(params => filter.translators.deliveryMethods.translate(params.data?.deliveryMethod))
        .withWidth(90);
    builder.for(OfferColumn.freightFee, localize("Column_Header_FreightFee"), "freightFee", localize("Column_Description_FreightFee"))
        .withFormatter(formatPricingValue)
        .asRightAligned()
        .withWidth(80);
    builder.for(OfferColumn.orderId, localize("Column_Header_HedgeOrderId"), null, localize("Column_Description_HedgeOrderId"), { hide: filter.isCustomer })
        .withGetter(params => canShowOrderId(params.data?.order?.status) ? params.data?.order?.displayId : "")
        .withFormatter(formatOrderDisplayId)
        .withTooltipValueGetter(params => canShowOrderId(params.data?.order?.status) ? params.data?.order?.displayId : "")
        .withWidth(90);
    builder.for(OfferColumn.fcmAccountNumber, localize("Column_Header_FcmAccountNumber"), "order.fcmAccountNumber", localize("Column_Description_FcmAccountNumber"), { hide: filter.isCustomer })
        .withWidth(90);
    builder.for(OfferColumn.createdBy, localize("Column_Header_CreatedBy"), "createdBy.name", localize("Column_Description_CreatedBy"))
        .withWidth(150);
    if (filter.merchant.generalSettings.displayPaymentOption) {
        builder.for(OfferColumn.paymentOption, localize("Column_Header_PaymentOption"), null, localize("Column_Description_PaymentOption"))
            .withGetter(params => filter.translators.paymentOptions.translate(params.data?.paymentOption))
            .withWidth(120);
    }
    return builder;
}

export const defaultOfferColumns: OfferColumnConfigurations = {
    working: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customerContractId,
            OfferColumn.orderId,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.position,
            OfferColumn.location,
            OfferColumn.inventoryLocation,
            OfferColumn.contractLocation,
            OfferColumn.customerWillSell,
            OfferColumn.offerType,
            OfferColumn.hedgeType,
            OfferColumn.hedgeStatus,
            OfferColumn.quantity,
            OfferColumn.cashPrice,
            OfferColumn.futuresPrice,
            OfferColumn.myBasis,
            OfferColumn.contractSymbol,
            OfferColumn.toHit,
            OfferColumn.deliveryPeriod,
            OfferColumn.createdDate,
            OfferColumn.offerExpiration,
            OfferColumn.trader,
            OfferColumn.lastUpdatedDate
        ]
    },
    pending: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customerContractId,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.position,
            OfferColumn.location,
            OfferColumn.inventoryLocation,
            OfferColumn.contractLocation,
            OfferColumn.customerWillSell,
            OfferColumn.offerType,
            OfferColumn.orderType,
            OfferColumn.hedgeStatus,
            OfferColumn.quantity,
            OfferColumn.cashPrice,
            OfferColumn.futuresPrice,
            OfferColumn.myBasis,
            OfferColumn.contractSymbol,
            OfferColumn.toHit,
            OfferColumn.deliveryPeriod,
            OfferColumn.offerExpiration,
            OfferColumn.trader,
            OfferColumn.lastUpdatedDate
        ]
    },
    filled: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customerContractId,
            OfferColumn.orderId,
            OfferColumn.comments,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.position,
            OfferColumn.location,
            OfferColumn.inventoryLocation,
            OfferColumn.contractLocation,
            OfferColumn.customerWillSell,
            OfferColumn.offerType,
            OfferColumn.hedgeType,
            OfferColumn.quantity,
            OfferColumn.fillQuantity,
            OfferColumn.futuresPrice,
            OfferColumn.fillPrice,
            OfferColumn.basis,
            OfferColumn.contractSymbol,
            OfferColumn.cashPrice,
            OfferColumn.currency,
            OfferColumn.finalPrice,
            OfferColumn.deliveryPeriod,
            OfferColumn.trader,
            OfferColumn.lastUpdatedDate,
            OfferColumn.fillTime
        ]
    },
    cancelled: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customerContractId,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.position,
            OfferColumn.location,
            OfferColumn.inventoryLocation,
            OfferColumn.contractLocation,
            OfferColumn.customerWillSell,
            OfferColumn.offerType,
            OfferColumn.hedgeType,
            OfferColumn.hedgeStatus,
            OfferColumn.quantity,
            OfferColumn.cashPrice,
            OfferColumn.futuresPrice,
            OfferColumn.basis,
            OfferColumn.contractSymbol,
            OfferColumn.deliveryPeriod,
            OfferColumn.trader,
            OfferColumn.lastUpdatedDate,
            OfferColumn.cancelledDate,
            OfferColumn.comments
        ]
    },
    expired: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customerContractId,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.position,
            OfferColumn.location,
            OfferColumn.inventoryLocation,
            OfferColumn.contractLocation,
            OfferColumn.offerType,
            OfferColumn.hedgeType,
            OfferColumn.hedgeStatus,
            OfferColumn.quantity,
            OfferColumn.cashPrice,
            OfferColumn.futuresPrice,
            OfferColumn.basis,
            OfferColumn.contractSymbol,
            OfferColumn.deliveryPeriod,
            OfferColumn.offerExpiration,
            OfferColumn.trader,
            OfferColumn.lastUpdatedDate
        ]
    }
}

export const mobileColumns: OfferColumnConfigurations = {
    working: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customer,
            OfferColumn.offerType_mobile,
            OfferColumn.quantity,
            OfferColumn.contractSymbol,
            OfferColumn.price_mobile,
            OfferColumn.toHit
        ]
    },
    pending: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.offerType,
            OfferColumn.quantity,
            OfferColumn.contractSymbol,
            OfferColumn.price_mobile,
            OfferColumn.toHit
        ]
    },
    filled: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.offerType,
            OfferColumn.quantity,
            OfferColumn.contractSymbol,
            OfferColumn.price_mobile
        ]
    },
    cancelled: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.offerType,
            OfferColumn.quantity,
            OfferColumn.contractSymbol,
            OfferColumn.price_mobile
        ]
    },
    expired: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            OfferColumn.id,
            OfferColumn.customer,
            OfferColumn.commodity,
            OfferColumn.offerType,
            OfferColumn.quantity,
            OfferColumn.contractSymbol,
            OfferColumn.price_mobile
        ]
    }
}